import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { MenuProps } from './Menu';
import getIcon from '../Icon/getIcon';
import { AllInOneMenu } from '..';
export interface IconMenuProps extends MenuProps {
  icon: string;
  iconClassName?: string;
  horizontalDirection?: AllInOneMenu.HorizontalDirection;
  verticalDirection?: AllInOneMenu.VerticalDirection;
  menuKey?: number | string;
}

/**
 * An IconMenu allows for a div wrapped icon to be clicked to show and hide
 * an AllInOneMenu.Menu. Based on the direction(s) specified, the menu is
 * positioned relative to the icon.
 *
 * HorizontalDirection.LeftToRight - the left edges of the icon and menu are aligned
 * HorizontalDirection.RightRoLeft - the right edges of the icon and menu are aligned
 * VerticalDirection.TopToBottom - the top edge of the menu appears directly below the bottom edge of the icon
 * VerticalDirection.BottomToTop - the bottom edge of the menu appears directly above the top edge of the icon
 *
 * For example, if an IconMenu were situated in the bottom-left corner of a container,
 * it would be best to use BottomToTop and LeftToRight directions for it.
 */
export default function IconMenu({
  icon,
  iconClassName,
  horizontalDirection,
  verticalDirection,
  children,
  backLabel,
  menuClassName,
  menuStyle,
  onVisibilityChange,
  menuKey,
}: IconMenuProps) {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState<CSSProperties>({});
  const iconRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuPosition = useCallback(() => {
    if (!iconRef.current) {
      return;
    }

    const rect = iconRef.current.getBoundingClientRect();
    const position: CSSProperties = {};

    if (verticalDirection === AllInOneMenu.VerticalDirection.BottomToTop) {
      position.bottom = window.innerHeight - rect.top;
    } else {
      position.top = rect.bottom;
    }

    if (horizontalDirection === AllInOneMenu.HorizontalDirection.LeftToRight) {
      position.left = Math.max(4, rect.left);
    } else {
      position.right = Math.max(4, window.innerWidth - rect.right);
    }

    setMenuPosition(position);
  }, [horizontalDirection, verticalDirection]);

  const toggleMenuVisibility = useCallback(() => {
    if (!isMenuVisible) {
      updateMenuPosition();
    }
    setIsMenuVisible(!isMenuVisible);
  }, [isMenuVisible, updateMenuPosition]);

  useEffect(() => {
    if (!isMenuVisible) {
      return;
    }

    const handleOutsideInteraction = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (!iconRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setIsMenuVisible(false);
      }
    };

    const handleViewportChange = () => updateMenuPosition();

    document.addEventListener('mousedown', handleOutsideInteraction);
    document.addEventListener('touchstart', handleOutsideInteraction);
    window.addEventListener('resize', handleViewportChange);
    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction);
      document.removeEventListener('touchstart', handleOutsideInteraction);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [isMenuVisible, updateMenuPosition]);

  const portalElement = document.getElementById('react-portal');
  const menu =
    isMenuVisible && portalElement
      ? ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] max-w-[calc(100vw-8px)]"
            style={menuPosition}
          >
            <AllInOneMenu.Menu
              key={menuKey}
              isVisible
              backLabel={backLabel}
              menuClassName={menuClassName}
              menuStyle={menuStyle}
              onVisibilityChange={isVisible => {
                setIsMenuVisible(isVisible);
                onVisibilityChange?.(isVisible);
              }}
              horizontalDirection={horizontalDirection}
            >
              {children}
            </AllInOneMenu.Menu>
          </div>,
          portalElement
        )
      : null;

  return (
    <>
      <div
        ref={iconRef}
        className="relative"
      >
        <div
          className={iconClassName}
          onClick={toggleMenuVisibility}
        >
          {getIcon(icon)}
        </div>
      </div>
      {menu}
    </>
  );
}
